import { Printer } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { cn } from '@/utils/cn'
import { formatDate, formatPercent } from '@/utils/format'
import { sectionLabel } from '@/features/academic/sectionLabel'
import { formatMarks, formatOutOf, formatScore } from '../status'
import type { ReportCard } from '../types'
import { GradeBadge } from './ExamBadges'

/** Printable report card (Tailwind print: variants flatten colours and chrome on paper). */
export function ReportCardView({ card }: { card: ReportCard }) {
  const outOf = card.outOf ?? card.result.outOf ?? null
  if (outOf !== null)
    return <ReportCardFrame card={card} table={<WeightedTable card={card} outOf={outOf} />} />
  return <ReportCardFrame card={card} table={<PercentageTable card={card} />} />
}

function ReportCardFrame({ card, table }: { card: ReportCard; table: ReactNode }) {
  const { t } = useTranslation('exams')
  const r = card.result
  const outOf = card.outOf ?? r.outOf ?? null
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        {card.isPreview ? (
          <Alert tone="info" className="flex-1">
            {t('Provisional: calculated from the marks entered so far; results are not published yet.')}
          </Alert>
        ) : (
          <span />
        )}
        <Button variant="secondary" leftIcon={<Printer />} onClick={() => window.print()}>
          {t('Print')}
        </Button>
      </div>

      <Card
        id="exam-report-card"
        data-print-document
        className="overflow-hidden print:rounded-none print:border-0 print:shadow-none"
      >
        <header className="bg-brand-gradient px-6 py-5 text-white print:bg-none print:px-0 print:text-ink">
          <p className="text-xs tracking-wider uppercase opacity-80">{card.school.name}</p>
          <h2 className="mt-1 text-xl font-semibold">
            {t('Report card · {{name}}', { name: card.exam.name })}
          </h2>
          <p className="mt-0.5 text-sm opacity-85">
            {card.exam.examType} · {card.exam.academicYear} · {formatDate(card.exam.startsOn)} –{' '}
            {formatDate(card.exam.endsOn)}
          </p>
        </header>

        <dl className="grid gap-4 border-b border-line px-6 py-4 text-sm sm:grid-cols-4 print:grid-cols-4 print:px-0">
          <div className="sm:col-span-2 print:col-span-2">
            <dt className="text-xs text-muted">{t('Student')}</dt>
            <dd className="font-semibold text-ink">{card.student.fullName}</dd>
            <dd className="text-xs text-muted">
              {card.student.studentCode} · {t('Adm. {{number}}', { number: card.student.admissionNumber })}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted">{t('Class')}</dt>
            <dd className="font-medium text-ink">
              {sectionLabel(card.student.className, card.student.sectionName, ' – ')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted">{t('Roll')}</dt>
            <dd className="font-medium text-ink tabular">{card.student.rollNumber ?? '—'}</dd>
          </div>
        </dl>

        {table}

        <div className="grid gap-4 border-t border-line px-6 py-5 sm:grid-cols-4 print:grid-cols-4 print:px-0">
          {outOf !== null ? (
            <Summary
              label={t('Overall average')}
              value={
                <>
                  {formatOutOf(r.average, outOf, 2)}{' '}
                  {r.grade ? <span className="text-sm font-medium text-muted">· {r.grade}</span> : null}
                </>
              }
            />
          ) : (
            <Summary label={t('Percentage')} value={formatPercent(r.percentage)} />
          )}
          <Summary
            label={t('Result')}
            value={
              <Badge tone={r.isPass ? 'success' : 'danger'} size="md">
                {r.isPass ? t('Passed') : t('Not passed')}
              </Badge>
            }
          />
          <Summary
            label={t('Rank in section')}
            value={t('{{rank}} of {{size}}', { rank: r.sectionRank, size: card.sectionSize })}
          />
          <Summary
            label={t('Rank in class')}
            value={t('{{rank}} of {{size}}', { rank: r.classRank, size: card.classSize })}
          />
        </div>

        <footer className="border-t border-line bg-surface-muted/60 px-6 py-4 text-xs text-muted print:bg-transparent print:px-0">
          <p className="font-medium text-ink-soft">
            {t('Grading scale: {{name}}', { name: card.gradingScale.name })}
          </p>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {card.gradingScale.bands.map((b) => (
              <span key={b.label} className="tabular">
                <span className="font-semibold text-ink-soft">{b.label}</span>{' '}
                {outOf !== null
                  ? `${formatScore((b.minPercent * outOf) / 100)}–${formatScore((b.maxPercent * outOf) / 100)} / ${formatScore(outOf)}`
                  : `${formatMarks(b.minPercent)}–${formatMarks(b.maxPercent)}%`}
                {b.gradePoint !== null ? ` · ${b.gradePoint.toFixed(1)}` : ''}
              </span>
            ))}
          </p>
          <p className="mt-2">
            {outOf !== null
              ? t(
                  'Each mark is converted to {{outOf}} and multiplied by the subject coefficient; an absent paper counts as 0. The overall average is the weighted total divided by the total coefficients, and its grade decides the result.',
                  { outOf },
                )
              : t(
                  'A paper is passed at or above its pass mark; an absent paper counts as failed. The overall result requires every paper to be passed.',
                )}
          </p>
          {card.exam.publishedAt ? (
            <p className="mt-1">{t('Published {{date}}', { date: formatDate(card.exam.publishedAt) })}</p>
          ) : null}
        </footer>
      </Card>
    </div>
  )
}

function PercentageTable({ card }: { card: ReportCard }) {
  const { t } = useTranslation('exams')
  const r = card.result
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <caption className="sr-only">{t('Marks by subject')}</caption>
        <thead>
          <tr className="text-left text-2xs font-semibold tracking-wider text-muted uppercase">
            <th scope="col" className="border-b border-line bg-surface-muted px-4 py-2.5 pl-6 print:pl-0">
              {t('Subject')}
            </th>
            <th scope="col" className="border-b border-line bg-surface-muted px-4 py-2.5 text-right">
              {t('Marks')}
            </th>
            <th
              scope="col"
              className="hidden border-b border-line bg-surface-muted px-4 py-2.5 text-right sm:table-cell print:table-cell"
            >
              {t('Pass', { context: 'mark' })}
            </th>
            <th scope="col" className="border-b border-line bg-surface-muted px-4 py-2.5 text-center">
              {t('Grade')}
            </th>
            <th
              scope="col"
              className="hidden border-b border-line bg-surface-muted px-4 py-2.5 text-right sm:table-cell print:table-cell"
            >
              {t('GP')}
            </th>
            <th
              scope="col"
              className="hidden border-b border-line bg-surface-muted px-4 py-2.5 pr-6 md:table-cell print:table-cell"
            >
              {t('Remarks')}
            </th>
          </tr>
        </thead>
        <tbody>
          {r.papers.map((p) => (
            <tr key={p.paperId}>
              <td className="border-b border-line px-4 py-2.5 pl-6 font-medium text-ink print:pl-0">
                {p.subjectName}
              </td>
              <td
                className={cn(
                  'border-b border-line px-4 py-2.5 text-right tabular',
                  !p.isPass && 'text-danger-700',
                )}
              >
                {p.isAbsent ? t('Absent') : p.isMissing ? '—' : formatMarks(p.marksObtained)}{' '}
                <span className="text-muted">/ {formatMarks(p.maxMarks)}</span>
              </td>
              <td className="hidden border-b border-line px-4 py-2.5 text-right text-muted tabular sm:table-cell print:table-cell">
                {formatMarks(p.passMarks)}
              </td>
              <td className="border-b border-line px-4 py-2.5 text-center">
                <GradeBadge grade={p.grade} isPass={p.isPass} isAbsent={p.isAbsent} isMissing={p.isMissing} />
              </td>
              <td className="hidden border-b border-line px-4 py-2.5 text-right tabular sm:table-cell print:table-cell">
                {p.gradePoint === null ? '—' : p.gradePoint.toFixed(2)}
              </td>
              <td className="hidden border-b border-line px-4 py-2.5 pr-6 text-ink-soft md:table-cell print:table-cell">
                {p.remarks ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold text-ink">
            <th scope="row" className="px-4 py-3 pl-6 text-left print:pl-0">
              {t('Total')}
            </th>
            <td className="px-4 py-3 text-right tabular">
              {formatMarks(r.totalObtained)}{' '}
              <span className="font-normal text-muted">/ {formatMarks(r.totalMax)}</span>
            </td>
            <td className="hidden sm:table-cell print:table-cell" />
            <td className="px-4 py-3 text-center">
              <GradeBadge grade={r.grade} isPass={r.isPass} size="md" />
            </td>
            <td className="hidden px-4 py-3 text-right tabular sm:table-cell print:table-cell">
              {r.gradePoint === null ? '—' : r.gradePoint.toFixed(2)}
            </td>
            <td className="hidden md:table-cell print:table-cell" />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** Out-of-N bulletin: mark /N, coefficient, mark × coefficient, then the weighted totals. */
function WeightedTable({ card, outOf }: { card: ReportCard; outOf: number }) {
  const { t } = useTranslation('exams')
  const r = card.result
  const head = 'border-b border-line bg-surface-muted px-4 py-2.5'
  const cell = 'border-b border-line px-4 py-2.5'
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <caption className="sr-only">{t('Marks by subject')}</caption>
        <thead>
          <tr className="text-left text-2xs font-semibold tracking-wider text-muted uppercase">
            <th scope="col" className={cn(head, 'pl-6 print:pl-0')}>
              {t('Subject')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('Mark')} <span className="normal-case">/ {formatScore(outOf)}</span>
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('Coef.')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('Mark × coef.')}
            </th>
            <th scope="col" className={cn(head, 'hidden pr-6 md:table-cell print:table-cell')}>
              {t('Remarks')}
            </th>
          </tr>
        </thead>
        <tbody>
          {r.papers.map((p) => (
            <tr key={p.paperId}>
              <td className={cn(cell, 'pl-6 font-medium text-ink print:pl-0')}>
                {p.subjectName}
                {p.maxMarks !== outOf && !p.isAbsent && !p.isMissing ? (
                  <span className="block text-xs font-normal text-muted tabular">
                    {t('{{marks}} out of {{max}}', {
                      marks: formatScore(p.marksObtained),
                      max: formatScore(p.maxMarks),
                    })}
                  </span>
                ) : null}
              </td>
              <td className={cn(cell, 'text-right tabular', !p.isPass && 'text-danger-700')}>
                {p.isAbsent ? t('Absent') : p.isMissing ? '—' : formatScore(p.note)}
              </td>
              <td className={cn(cell, 'text-right tabular')}>{formatScore(p.coefficient)}</td>
              <td className={cn(cell, 'text-right font-medium text-ink tabular')}>
                {p.isMissing ? '—' : formatScore(p.weighted)}
              </td>
              <td className={cn(cell, 'hidden pr-6 text-ink-soft md:table-cell print:table-cell')}>
                {p.remarks ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold text-ink">
            <th scope="row" className="px-4 py-3 pl-6 text-left print:pl-0">
              {t('Total')}
            </th>
            <td className="px-4 py-3" />
            <td className="px-4 py-3 text-right tabular">{formatScore(r.coefficientTotal)}</td>
            <td className="px-4 py-3 text-right tabular">
              {formatScore(r.weightedTotal)}{' '}
              <span className="font-normal text-muted">/ {formatScore(r.maxWeightedTotal)}</span>
            </td>
            <td className="hidden md:table-cell print:table-cell" />
          </tr>
        </tfoot>
      </table>
      <p className="border-t border-line px-6 py-3 text-sm text-ink-soft tabular print:px-0">
        {t('Weighted total: {{total}} / {{max}}', {
          total: formatScore(r.weightedTotal),
          max: formatScore(r.maxWeightedTotal),
        })}
        {' · '}
        {t('Total coefficients: {{total}}', { total: formatScore(r.coefficientTotal) })}
        {' · '}
        <span className="font-semibold text-ink">
          {t('Overall average: {{average}}', { average: formatOutOf(r.average, outOf, 2) })}
        </span>
      </p>
    </div>
  )
}

function Summary({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted">{label}</p>
      <div className="mt-0.5 text-lg font-semibold text-ink tabular">{value}</div>
    </div>
  )
}
